import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * TOTP 2FA panel for /studio/account.
 *
 * Backend contract:
 *   GET    /admin/2fa/status                                -> { totp_enabled, recovery_codes_remaining }
 *   POST   /admin/2fa/setup                                 -> { secret, otpauth_uri, qr_svg, issuer, account }
 *   POST   /admin/2fa/activate            { secret, code }  -> { ok, recovery_codes[] }   (ONE-TIME)
 *   POST   /admin/2fa/disable             { password, code }-> { ok }
 *   POST   /admin/2fa/recovery/regenerate { password, code }-> { ok, recovery_codes[] }   (ONE-TIME)
 *
 * The plain-text recovery codes returned by `activate` and `recovery/regenerate`
 * are shown to the admin ONCE. Backend stores only bcrypt hashes.
 */
export default function TwoFactorPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  // Setup flow state
  const [setupData, setSetupData] = useState(null); // { secret, otpauth_uri, qr_svg }
  const [activateCode, setActivateCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Disable flow state
  const [showDisable, setShowDisable] = useState(false);
  const [disablePw, setDisablePw] = useState("");
  const [disableCode, setDisableCode] = useState("");

  // Regenerate flow state
  const [showRegen, setShowRegen] = useState(false);
  const [regenPw, setRegenPw] = useState("");
  const [regenCode, setRegenCode] = useState("");

  const refresh = async () => {
    try {
      const r = await api.get("/admin/2fa/status");
      setStatus(r.data);
    } catch {
      setStatus({ totp_enabled: false, recovery_codes_remaining: 0 });
    }
  };

  useEffect(() => { refresh(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/2fa/setup");
      setSetupData(r.data);
      setActivateCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start 2FA setup");
    } finally {
      setBusy(false);
    }
  };

  const activate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/2fa/activate", {
        secret: setupData.secret,
        code: activateCode.replace(/\s+/g, ""),
      });
      setRecoveryCodes(r.data.recovery_codes);
      setSetupData(null);
      setActivateCode("");
      toast.success("Two-factor authentication enabled.");
      await refresh();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Activation failed");
    } finally {
      setBusy(false);
    }
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/2fa/disable", { password: disablePw, code: disableCode.trim() });
      toast.success("Two-factor authentication disabled.");
      setShowDisable(false);
      setDisablePw("");
      setDisableCode("");
      await refresh();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not disable 2FA");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/2fa/recovery/regenerate", {
        password: regenPw,
        code: regenCode.trim(),
      });
      setRecoveryCodes(r.data.recovery_codes);
      setShowRegen(false);
      setRegenPw("");
      setRegenCode("");
      toast.success("New recovery codes generated. Previous codes are now invalid.");
      await refresh();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not regenerate recovery codes");
    } finally {
      setBusy(false);
    }
  };

  const copyAllCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard?.writeText(recoveryCodes.join("\n")).then(
      () => toast.success("Recovery codes copied to clipboard."),
      () => toast.error("Could not copy. Please copy them manually.")
    );
  };

  if (status === null) {
    return (
      <div data-testid="twofa-panel" className="mt-16 pt-12 border-t border-mist/60">
        <p className="eyebrow">Two-factor authentication</p>
        <p className="mt-4 text-ink/55 text-[14px]">Loading…</p>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // After activation / regeneration: show the new recovery codes ONCE.
  // -----------------------------------------------------------------------
  if (recoveryCodes) {
    return (
      <div data-testid="twofa-panel" className="mt-16 pt-12 border-t border-mist/60 max-w-2xl">
        <p className="eyebrow text-copper">Recovery codes — shown once</p>
        <h2 className="mt-3 font-serif text-3xl text-jade font-light">Save these now.</h2>
        <p className="mt-3 text-ink/70 text-[14px] font-light">
          Each code lets you sign in once if you lose your authenticator. Store them in your password
          manager. They will not be shown again. Generate fresh codes from this page if you ever use one.
        </p>
        <div
          data-testid="twofa-recovery-codes"
          className="mt-8 grid grid-cols-2 gap-3 bg-sand border border-mist px-6 py-6 font-mono text-[15px] tracking-wider text-jade"
        >
          {recoveryCodes.map((c) => (
            <code key={c} className="select-all">{c}</code>
          ))}
        </div>
        <div className="mt-8 flex gap-4">
          <button
            type="button"
            data-testid="twofa-recovery-copy"
            onClick={copyAllCodes}
            className="btn-primary"
          >
            Copy all codes
          </button>
          <button
            type="button"
            data-testid="twofa-recovery-done"
            onClick={() => setRecoveryCodes(null)}
            className="text-[12px] tracking-[0.18em] uppercase text-ink/55 hover:text-jade"
          >
            I&apos;ve saved them
          </button>
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Setup wizard: shown after clicking "Enable 2FA".
  // -----------------------------------------------------------------------
  if (setupData) {
    return (
      <div data-testid="twofa-panel" className="mt-16 pt-12 border-t border-mist/60 max-w-2xl">
        <p className="eyebrow">Enable two-factor authentication</p>
        <h2 className="mt-3 font-serif text-3xl text-jade font-light">Scan, then enter the code.</h2>
        <p className="mt-3 text-ink/70 text-[14px] font-light">
          Open Google Authenticator, Microsoft Authenticator, or 1Password and scan the QR code below.
          Enter the 6-digit code it shows to finish setup.
        </p>
        <div
          data-testid="twofa-qr"
          className="mt-8 bg-ivory border border-mist p-6 w-fit"
          dangerouslySetInnerHTML={{ __html: setupData.qr_svg }}
        />
        <details className="mt-6 text-[13px] text-ink/55">
          <summary className="cursor-pointer">Can&apos;t scan? Show the secret to type manually</summary>
          <div className="mt-3 p-3 bg-sand border border-mist font-mono text-jade text-[13px] tracking-widest break-all" data-testid="twofa-secret">
            {setupData.secret}
          </div>
        </details>

        <form onSubmit={activate} className="mt-8 space-y-5 max-w-sm">
          <div>
            <label className="eyebrow">6-digit code</label>
            <input
              data-testid="twofa-activate-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              className="input-line mt-3 tracking-[0.3em] text-center text-lg"
              placeholder="123 456"
              value={activateCode}
              onChange={(e) => setActivateCode(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex gap-4">
            <button
              type="submit"
              data-testid="twofa-activate-submit"
              disabled={busy || !activateCode.trim()}
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? "Verifying…" : "Verify and enable"}
            </button>
            <button
              type="button"
              data-testid="twofa-activate-cancel"
              onClick={() => { setSetupData(null); setActivateCode(""); }}
              className="text-[12px] tracking-[0.18em] uppercase text-ink/55 hover:text-jade"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Default panel: enabled / disabled status + actions.
  // -----------------------------------------------------------------------
  return (
    <div data-testid="twofa-panel" className="mt-16 pt-12 border-t border-mist/60 max-w-2xl">
      <p className="eyebrow">Two-factor authentication</p>
      <h2 className="mt-3 font-serif text-3xl text-jade font-light">
        {status.totp_enabled ? "2FA is on." : "2FA is off."}
      </h2>
      <p className="mt-3 text-ink/70 text-[14px] font-light">
        {status.totp_enabled
          ? `Your authenticator app is required to sign in. ${status.recovery_codes_remaining} recovery code${status.recovery_codes_remaining === 1 ? "" : "s"} remaining.`
          : "Add a six-digit code from your authenticator app to every sign-in. Strongly recommended for admins."}
      </p>

      {!status.totp_enabled && (
        <button
          type="button"
          data-testid="twofa-enable-btn"
          onClick={startSetup}
          disabled={busy}
          className="btn-primary mt-8 disabled:opacity-50"
        >
          {busy ? "Working…" : "Enable two-factor"}
        </button>
      )}

      {status.totp_enabled && (
        <div className="mt-8 flex flex-wrap gap-4">
          {!showDisable && !showRegen && (
            <>
              <button
                type="button"
                data-testid="twofa-regen-btn"
                onClick={() => setShowRegen(true)}
                className="border border-mist hover:border-jade text-jade px-5 py-2 text-[12px] tracking-[0.18em] uppercase"
              >
                Regenerate recovery codes
              </button>
              <button
                type="button"
                data-testid="twofa-disable-btn"
                onClick={() => setShowDisable(true)}
                className="border border-mist hover:border-copper text-copper px-5 py-2 text-[12px] tracking-[0.18em] uppercase"
              >
                Disable two-factor
              </button>
            </>
          )}

          {showDisable && (
            <form onSubmit={disable} className="w-full space-y-5 max-w-sm">
              <p className="eyebrow text-copper">Confirm disable</p>
              <div>
                <label className="eyebrow text-ink/55">Current password</label>
                <input
                  data-testid="twofa-disable-pw"
                  type="password" required autoComplete="current-password"
                  className="input-line mt-3"
                  value={disablePw} onChange={(e) => setDisablePw(e.target.value)} />
              </div>
              <div>
                <label className="eyebrow text-ink/55">6-digit code or recovery code</label>
                <input
                  data-testid="twofa-disable-code"
                  type="text" required autoComplete="one-time-code"
                  className="input-line mt-3"
                  value={disableCode} onChange={(e) => setDisableCode(e.target.value)} />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit" data-testid="twofa-disable-submit" disabled={busy}
                  className="btn-primary disabled:opacity-40">
                  {busy ? "Disabling…" : "Disable two-factor"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDisable(false); setDisablePw(""); setDisableCode(""); }}
                  className="text-[12px] tracking-[0.18em] uppercase text-ink/55 hover:text-jade">
                  Cancel
                </button>
              </div>
            </form>
          )}

          {showRegen && (
            <form onSubmit={regenerate} className="w-full space-y-5 max-w-sm">
              <p className="eyebrow">Regenerate recovery codes</p>
              <p className="text-ink/65 text-[13px]">
                Previous recovery codes will stop working immediately.
              </p>
              <div>
                <label className="eyebrow text-ink/55">Current password</label>
                <input
                  data-testid="twofa-regen-pw"
                  type="password" required autoComplete="current-password"
                  className="input-line mt-3"
                  value={regenPw} onChange={(e) => setRegenPw(e.target.value)} />
              </div>
              <div>
                <label className="eyebrow text-ink/55">6-digit code or recovery code</label>
                <input
                  data-testid="twofa-regen-code"
                  type="text" required autoComplete="one-time-code"
                  className="input-line mt-3"
                  value={regenCode} onChange={(e) => setRegenCode(e.target.value)} />
              </div>
              <div className="flex gap-3">
                <button
                  type="submit" data-testid="twofa-regen-submit" disabled={busy}
                  className="btn-primary disabled:opacity-40">
                  {busy ? "Generating…" : "Generate new codes"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowRegen(false); setRegenPw(""); setRegenCode(""); }}
                  className="text-[12px] tracking-[0.18em] uppercase text-ink/55 hover:text-jade">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
